/**
 * A menu from whatever spreadsheet the venue has.
 *
 * Specification: ../test/menu-import.spec.ts.
 *
 * Read in the browser, mapped by a person, sent as rows to
 * `POST /v1/menu/import`. Nothing here knows Storyous's export: the columns are
 * guessed from their headings and the owner corrects the guess.
 *
 * THE TAX IS WHAT THE ITEM IS, chosen per category. A file's rate cannot
 * decide it: sitting down, beer and Kofola are both 21 % in Czechia, yet one
 * is alcohol (18+, 21 % to take away) and the other is not (12 % to take away).
 * So the rate is checked against what the registry charges, and where the two
 * disagree the row is listed for a person to look at.
 */

export type ImportTax = "FOOD" | "NON_ALC_BEVERAGE" | "ALCOHOL" | "TAP_WATER" | "MILK_BEVERAGE" | "NON_FOOD";

export interface ColumnMapping {
  /** The first row is headings, not an item. */
  readonly header: boolean;
  readonly name: number | null;
  readonly price: number | null;
  readonly category: number | null;
  readonly vat: number | null;
}

export interface PlannedItem {
  /** The file's own line, for a refusal to name. */
  readonly line: number;
  readonly name: string;
  /** Minor units, as the API takes money. */
  readonly grossMinor: string;
  readonly taxCategory: ImportTax;
  readonly stationId: string;
  readonly category: string | null;
  readonly ageRestricted: boolean;
}

export interface ImportProblem {
  readonly line: number;
  readonly name: string;
  readonly reason: "name" | "price";
}

/** The file says one rate, the law another: imported, and shown to a person. */
export interface RateMismatch {
  readonly line: number;
  readonly name: string;
  /** Percent, as the file has it. */
  readonly fileRate: number;
  /** Percent, as the registry charges it sitting down. */
  readonly rate: number;
}

export interface ImportChoices {
  /** Category name ("" for none) → station. */
  readonly stations: ReadonlyMap<string, string>;
  /** Category name → tax; a category not in it gets the suggestion from its words. */
  readonly taxes: ReadonlyMap<string, ImportTax>;
  /** Basis points each tax costs sitting down, from the registry; absent, nothing is compared. */
  readonly ratesBp?: ReadonlyMap<ImportTax, number>;
}

// --- The file ---------------------------------------------------------------

export function decodeTable(bytes: Uint8Array): string {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes.subarray(2));
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes.subarray(2));
  const body = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? bytes.subarray(3) : bytes;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(body);
  } catch {
    // Not UTF-8: what Czech Excel writes when it saves CSV.
    return new TextDecoder("windows-1250").decode(body);
  }
}

/** Whichever of ; tab , the first line uses most, outside quotes; ties go to the Czech semicolon. */
const delimiterOf = (text: string): string => {
  const first = text.split(/\r?\n/).find((l) => l.trim() !== "") ?? "";
  const unquoted = first.replace(/"[^"]*"/g, "");
  let best = ";";
  let most = 0;
  for (const d of [";", "\t", ","]) {
    const n = unquoted.split(d).length - 1;
    if (n > most) {
      best = d;
      most = n;
    }
  }
  return best;
};

/** RFC 4180, with the delimiter the file uses. Blank rows go; cells are trimmed. */
export function parseTable(text: string): string[][] {
  const delimiter = delimiterOf(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const endCell = (): void => {
    row.push(cell.trim());
    cell = "";
  };
  const endRow = (): void => {
    endCell();
    if (row.some((c) => c !== "")) rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch !== '"') cell += ch;
      else if (text[i + 1] === '"') {
        cell += '"';
        i++;
      } else quoted = false;
    } else if (ch === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (ch === delimiter) endCell();
    else if (ch === "\n") endRow();
    else if (ch === "\r") {
      if (text[i + 1] !== "\n") endRow();
    } else cell += ch;
  }
  if (cell !== "" || row.length > 0) endRow();
  return rows;
}

// --- Cells ------------------------------------------------------------------

/**
 * Minor units from a price as people write it, or null.
 *
 * The last "," or "." followed by one or two digits is the decimal point. A
 * thousands separator is a space, an apostrophe, or whichever of "." and ","
 * is not the decimal point; with no decimals a comma is not one, because in
 * Czech "65,005" is not sixty-five thousand.
 */
export function parsePrice(cell: string): bigint | null {
  const s = cell
    .replace(/(kč|czk)\.?/gi, "")
    .replace(/[,.]-+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!/^\d[\d .,']*$/.test(s)) return null;
  const decimal = /^(.*?)([.,])(\d{1,2})$/.exec(s);
  const whole = decimal ? decimal[1]! : s;
  const fraction = decimal ? decimal[3]!.padEnd(2, "0") : "00";
  const thousands = decimal?.[2] === "." ? "[ ,']" : "[ .']";
  if (!new RegExp(`^\\d{1,3}(${thousands}?\\d{3})*$`).test(whole) && !/^\d+$/.test(whole)) return null;
  return BigInt(whole.replace(/[ .,']/g, "")) * 100n + BigInt(fraction);
}

/** A VAT rate in percent from "21", "21 %", "0,21", "12,00" — or null. */
export function readRate(cell: string): number | null {
  const s = cell.replace("%", "").replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Math.round((n > 0 && n < 1 ? n * 100 : n) * 100) / 100;
}

// --- Columns ----------------------------------------------------------------

const NAME = /^(název|nazev|name|položka|polozka|produkt|product|zboží|zbozi|artikl|item)/i;
const PRICE = /(cena|price|prodej)/i;
const PRICE_GROSS = /(s\s*dph|vč|vc\.|včetně|vcetne|brutto|gross|incl)/i;
const PRICE_NET = /(bez\s*dph|netto|\bnet\b|nákup|nakup|excl)/i;
const CATEGORY = /(kategorie|category|skupina|group|sekce|section)/i;
const VAT = /(dph|vat|sazba|tax)/i;

/**
 * Which column is which, from the headings — or, when the first row is not
 * headings, from what the columns hold (a name and a price; the rest is left
 * for the person to point at).
 */
export function guessColumns(first: readonly string[], sample: readonly (readonly string[])[] = []): ColumnMapping {
  const at = (re: RegExp, except: readonly number[] = []): number[] =>
    first.flatMap((h, i) => (re.test(h) && !except.includes(i) ? [i] : []));
  const prices = at(PRICE);
  const price =
    prices.find((i) => PRICE_GROSS.test(first[i]!)) ?? prices.find((i) => !PRICE_NET.test(first[i]!)) ?? prices[0] ?? null;
  const name = at(NAME)[0] ?? null;
  if (name !== null || price !== null) {
    return {
      header: true,
      name,
      price,
      category: at(CATEGORY)[0] ?? null,
      vat: at(VAT, prices)[0] ?? null,
    };
  }
  const rows = [first, ...sample];
  const columns = Math.max(...rows.map((r) => r.length));
  const share = (i: number, test: (cell: string) => boolean): number =>
    rows.filter((r) => test(r[i] ?? "")).length / rows.length;
  const indices = Array.from({ length: columns }, (_, i) => i);
  return {
    header: false,
    name: indices.find((i) => share(i, (c) => c !== "" && parsePrice(c) === null) >= 0.8) ?? null,
    price: indices.find((i) => share(i, (c) => parsePrice(c) !== null) >= 0.8) ?? null,
    category: null,
    vat: null,
  };
}

// --- Where it is made, how it is taxed ---------------------------------------

const DRINKS =
  /(pivo|piva|víno|vína|vino|nápoj|napoj|drink|nealko|káva|kava|čaj|caj|lihovin|koktejl|cocktail|limonád|limonad|džus|dzus|sodovk|minerál|voda|beer|wine|coffee|tea|spirit)/i;
const ALCOHOL =
  /(pivo|piva|víno|vína|vino|lihovin|koktejl|cocktail|alkohol|rum|vodka|\bgin\b|whisk|slivovic|becherov|beer|wine|spirit|prosecco|sekt|destilát|destilat|aperitiv|digestiv)/i;

/** Drinks to the bar, the rest to the kitchen, from the category's words. */
export function stationGuess(category: string | null, stations: readonly string[]): string {
  if (stations.length <= 1) return stations[0] ?? "";
  const bar = stations.find((s) => /bar|výčep|vycep|pult/i.test(s)) ?? stations[0]!;
  const kitchen = stations.find((s) => /kitchen|kuch/i.test(s)) ?? stations.find((s) => s !== bar) ?? stations[0]!;
  return category !== null && DRINKS.test(category) ? bar : kitchen;
}

/** A suggestion for a person to confirm. */
export function taxGuess(words: string | null): ImportTax {
  if (!words) return "FOOD";
  if (/nealko/i.test(words)) return "NON_ALC_BEVERAGE";
  if (ALCOHOL.test(words)) return "ALCOHOL";
  if (DRINKS.test(words)) return "NON_ALC_BEVERAGE";
  return "FOOD";
}

// --- The plan ---------------------------------------------------------------

export function planImport(
  rows: readonly (readonly string[])[],
  mapping: ColumnMapping,
  choices: ImportChoices,
): {
  readonly items: PlannedItem[];
  readonly problems: ImportProblem[];
  readonly mismatches: RateMismatch[];
  readonly categories: string[];
} {
  const items: PlannedItem[] = [];
  const problems: ImportProblem[] = [];
  const mismatches: RateMismatch[] = [];
  const categories: string[] = [];
  for (let r = mapping.header ? 1 : 0; r < rows.length; r++) {
    const row = rows[r]!;
    const line = r + 1;
    const cell = (i: number | null): string => (i === null ? "" : (row[i] ?? "").trim());
    const name = cell(mapping.name);
    const priceCell = cell(mapping.price);
    const category = cell(mapping.category) || null;
    if (!name && !priceCell) continue;
    if (category !== null && !categories.includes(category)) categories.push(category);
    if (!name) {
      problems.push({ line, name, reason: "name" });
      continue;
    }
    const price = parsePrice(priceCell);
    if (price === null) {
      problems.push({ line, name, reason: "price" });
      continue;
    }
    const key = category ?? "";
    const taxCategory = choices.taxes.get(key) ?? taxGuess(category);
    const fileRate = readRate(cell(mapping.vat));
    const lawBp = choices.ratesBp?.get(taxCategory);
    if (fileRate !== null && lawBp !== undefined && Math.round(fileRate * 100) !== lawBp) {
      mismatches.push({ line, name, fileRate, rate: lawBp / 100 });
    }
    items.push({
      line,
      name,
      grossMinor: price.toString(),
      taxCategory,
      stationId: choices.stations.get(key) ?? "",
      category,
      ageRestricted: taxCategory === "ALCOHOL",
    });
  }
  return { items, problems, mismatches, categories };
}
